<template>
	<span v-if="!isPopupOpen || !inModal">
		<legend
			v-if="!inModal"
			class="row g-2 p-0 pb-2 m-0"
		>
			<div class="text-center text-nowrap">
				Запит вимірювання потенціалу на вимкненній СКЗ
			</div>
		</legend>

		<div class="row g-0 gx-2 p-0 px-2 my-0">
			<div class="col">
				<Transition name="description">
					<span v-if="showDescription" class="my-0 mx-2">
						Загальний алгоритм вимірювання
						<ol class="">
							<li>Cиловий модуль станції знеструмлює електрод<sup>*</sup></li>
							<li>Очікується період стабілізації потенціалу</li>
							<li>Сервісний модуль вимірює потенціал на електроді</li>
							<li>Силовий модуль повертається до попереднього режиму роботи</li>
						</ol>
					</span>
				</Transition>
				<i>*&nbsp;Для силового модуля версії ВППС 2017 необхідно щоб був задаий параметр стабілізації. Якщо ні, то вимірювання не буде проведено.</i>
			</div>
		</div>

		<div class="row justify-content-center gx-2 gb-2 px-2 pb-2 my-1">
			<div class="col-auto me-0">
				<button
					class="btn btn-primary btn-sm"
					@click="onSubmit"
					:disabled="!isEnabled || !isSupported"
				>
					<span
						v-if="isPopupOpen"
						class="spinner-border spinner-border-sm"
						role="status"
						aria-hidden="true"
					>
					</span>
					Виміряти зараз
				</button>
			</div>
		</div>

		<div v-if="isSupported" 
			class="col-12 my-0 py-0"
		>
			<div id="valueHelpBlock" class="form-text">
				<p :class="helpPromtClass" class="mb-0">
					{{ helpPromt }}&nbsp;
				</p>
			</div>
		</div>

		<div v-if="!isSupported"
			class="col-12 text-nowrap text-secondary my-0 py-0"
		>
			*&nbsp;Встановлені силовий і сервісний модулі не можуть виконати цю дію
		</div>
	</span>
	
<!-- Modal -->
<confirmation-popup
	v-model:show="isPopupOpen"
	:confirm-button-title="isLoading || isError || allRpcs.length > 0 ? '' : 'Так'"
	:reject-button-title="isLoading || isError || allRpcs.length > 0 ? 'Закрити' : 'Ні'"
	@confirm="makeRequest()"
>
	<template v-if="station.isActive" v-slot:body>
		<template v-if="isLoading">
			<span
				class="spinner-border spinner-border-sm"
				role="status"
				aria-hidden="true"
			>
			</span>
			Перевірка активних задач телекерування
		</template>
		<template v-else-if="isError">
			Помилка при перевірці активних задач телекерування, закрийте і спробуйте ще.
		</template>
		<template v-else>
			<template v-if="allRpcs.length > 0">
				Станція зараз обробляє інший запит 
				("{{ rpcTaskSchema.job.findJob(allRpcs[0][rpcTaskSchema.job.key]).title }}"). 
				Будь ласка дочекайтесь його виконання.
			</template>
			<template v-else>
				Вимірювання почнеться <b>негайно</b>. 
				Під час вимірювання <b>стабілізація</b> параметрів <b>не буде</b> виконуватись.
			</template>
		</template>
	</template>
	<template v-else v-slot:body>
		Станція не активна. Тільки активні станції виконують запити.
	</template>
</confirmation-popup>
</template>




<script>
import ConfirmationPopup from "@/components/ConfirmationPopup.vue";
import { useAccontStore } from '@/stores/AccountStore'
import { useStationsStore } from '@/stores/StationsStore'
import {
	rpcTaskSchema,
	stationManageJobs,
	useRpcTasks,
} from '@/composables/rpcTasks.js'
import { Station } from '@/models/station.js'
import { PowerUnit } from "@/models/powerUnit.js"
import { ServiceUnit } from "@/models/serviceUnit.js"


export default {
	components: {
		ConfirmationPopup,
	},

	props: {
		isEnabled: {
			type: Boolean,
			required: false,
			default: true,
		},
		stationId: {
			type: String,
			required: true,
		},
		inModal: {
			type: Boolean,
			required: false,
			default: false,
		},
		showDescription: {
			type: Boolean,
			required: false,
			default: true,
		},
	},

	emits: {
		// Request creation result
		requestResult: value => typeof value === 'boolean',
	},

	setup() {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const {
			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		} = useRpcTasks()

		return {
			Station,
			rpcTaskSchema,

			accountStore,
			stationsStore,

			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		}
	},

	data() {
		return {
			// Confirmation popup
			isPopupOpen: false,

			// Help promt
			helpPromtClass: '',
			helpPromt: '',
		}
	},

	mounted () {},

	methods: {
		onSubmit () {
			// Begin fetching active tasks
			this.checkActiveTask()
			this.isPopupOpen = true
		},

		checkActiveTask() {
			this.setFilters({
				destinationStationIds: [this.stationId, ],
				isComplete: false,
				jobs: stationManageJobs,
			})
		},

		async makeRequest () {
			const result = await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.measurePotentialOnOffStation.key,
				this.stationId,
			)
			this.$emit('requestResult', Boolean(result))

			if (result) {
				this.printHint('Запит надіслано. Спостерігайте за його станом.')
			} else {
				this.printErr('Сервер відхилив запит постановки завдання.')
			}
		},

		printHint (msg) {
			this.helpPromtClass = "text-secondary"
			this.helpPromt = msg
		},

		printErr (msg) {
			this.helpPromtClass = "text-danger"
			this.helpPromt = msg
		},

	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		isSupported() {
			// If no service or power unit (undefined)
			if (!this.station.passport.has(Station.passportKeys.serviceUnit.self)
				|| !this.station.passport.has(Station.passportKeys.powerUnit.self)
			) {
				return false
			}

			const isServiceUnitSupport = [
				ServiceUnit.versions['2022-1'],
				ServiceUnit.versions['2022-2'],
				ServiceUnit.versions['2024-1'],
				ServiceUnit.versions['2024-2'],
				ServiceUnit.versions['2025-1'],
				ServiceUnit.versions['2025-2'],
				ServiceUnit.versions['2025-3'],
			].includes(this.station.passport.get(Station.passportKeys.serviceUnit.self)[Station.passportKeys.serviceUnit.unitVersion])

			const isPowerUnitSupport = [
				PowerUnit.versions.v2017,
				PowerUnit.versions.v2022,
				PowerUnit.versions.v2025,
				PowerUnit.versions.v2025t,
				PowerUnit.versions.mcps,
			].includes(this.station.passport.get(Station.passportKeys.powerUnit.self)[Station.passportKeys.powerUnit.unitVersion])

			return isServiceUnitSupport && isPowerUnitSupport
		},
	},
}
</script>




<style src="@/assets/stationForm.css" scoped></style>
<style scoped>
</style>